import { useState, type FormEvent } from 'react'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { Button } from '../../platform/primitives/Button'
import { Field } from '../../platform/primitives/Field'
import { emit } from '../../platform/analytics/analytics'
import { analyticsCode, authErrorFor } from '../../auth/errors'
import { AuthFailure } from '../../auth/port'
import { useAuth } from '../../auth/useAuth'
import type { FieldError } from '../../auth/errors'
import { AuthCard } from './AuthCard'
import { ShieldCheck } from 'lucide-react'
import { FormError } from '../../auth/FormError'
import './auth.css'

/**
 * The confirmation, and the reason it says so little.
 *
 * M1-R3: the answer must be the same whether or not the address has an account.
 * Firebase closed that oracle itself — with email-enumeration protection on,
 * `sendPasswordReset` resolves for an unknown address — and the backend settled
 * the identical question the same way on the waitlist endpoint
 * (`expense-monitor-backend` #262: a duplicate signup returns a silent 200).
 * Rendering "no account uses this address" here would re-open it in the one
 * place a user does not have to be signed in to reach.
 */
const CONFIRMATION = 'If that address has an account, a reset link is on its way. Check your inbox, and your spam folder.'

/**
 * The one code that must NOT reach the user, because saying it IS the oracle.
 *
 * Treated as success rather than swallowed silently: the user's next action is
 * the same either way — go and look in their inbox — and any other answer for
 * this address is a *different* answer, which is precisely what M1-R3 forbids.
 * Firebase should not send this with enumeration protection enabled, but that
 * is a project setting rather than a guarantee this client holds, and this
 * screen is the last thing between the code and the user.
 *
 * A set of one, kept as a set because the shape is "codes that are answers
 * about who has an account" and the next one to arrive belongs beside it rather
 * than in a second `if`.
 *
 * `auth/invalid-email` is deliberately NOT here. It is a fact about the string
 * the user typed, not about who has an account, and withholding it would leave
 * someone who typed `sam@examle.test` waiting for an email nobody will send.
 */
const ENUMERATION_CODES = new Set(['auth/user-not-found'])

export function ForgotPassword() {
  const auth = useAuth()
  const [error, setError] = useState<FieldError | null>(null)
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const email = String(form.get('email') ?? '')

    setError(null)
    setBusy(true)
    try {
      await auth.sendPasswordReset(email)
      emit('password_reset_requested', { error_code: null })
      setSent(true)
    } catch (thrown) {
      const detail = thrown instanceof AuthFailure ? thrown.detail : authErrorFor('unexpected')
      if (ENUMERATION_CODES.has(detail.code)) {
        // Measured as a success too. An `error_code: 'user-not-found'` series
        // would carry in the dashboard exactly the oracle the screen refuses to
        // show, and anyone with access to it could enumerate addresses.
        emit('password_reset_requested', { error_code: null })
        setSent(true)
        return
      }
      setError(detail)
      emit('password_reset_requested', { error_code: analyticsCode(detail.code) })
    } finally {
      setBusy(false)
    }
  }

  if (sent) {
    return (
      <AuthCard
        title="Check your inbox"
        foot={<InlineLink to="/signin">← Back to sign in</InlineLink>}
      >
        {/* `status`, not `alert`: the request succeeded, and an assertive
            region interrupts a screen-reader user mid-sentence to tell them
            nothing went wrong.
            
            Rendered ONCE. A first pass also passed this sentence as the
            card's `subtitle`, so it sat in the DOM twice — visible copy plus an
            sr-only announcement — and a screen reader read it twice. The tests
            caught it as "found multiple elements", which is the same defect
            wearing a query error.
            
            The design's own screen for this state is `verify` ("Check your
            inbox"), a separate unbuilt screen; this is the sent branch of
            `forgot` wearing the same shell, not that screen. */}
        <p className="auth-card__note" role="status">
          {CONFIRMATION}
        </p>
      </AuthCard>
    )
  }

  return (
    <AuthCard
      title="Reset your password"
      subtitle="We'll email a link that works once and expires in 30 minutes."
      /* The design draws this as plain text with a `←`, not as a button. It is
         a link because it navigates, and `InlineLink` because it is standalone
         — the exemption that applies to the consent line does not reach here. */
      foot={<InlineLink to="/signin">← Back to sign in</InlineLink>}
    >
      {/* `noValidate`, so the browser's bubble does not pre-empt M1-R2's
          messages. */}
      <form className="auth-form" onSubmit={(e) => void onSubmit(e)} noValidate>
        <Field
          size="form"
          label="Email"
          name="email"
          type="email"
          autoComplete="username"
          error={error?.field === 'email' ? error.message : undefined}
        />
        <FormError error={error} />
        <Button type="submit" variant="primary" size="form" disabled={busy}>
          {/* "Send the link", the design's wording — definite, because the
              subtitle one line above has just told them which link. */}
          {busy ? 'Sending…' : 'Send the link'}
        </Button>
      </form>

      {/**
       * The reassurance panel, transcribed. It answers the question this screen
       * actually provokes — a household product where a reset might plausibly
       * disconnect banks or sign out the other members.
       *
       * The icon is decorative and `aria-hidden`: the sentence beside it says
       * everything, and an announced shield adds a word with no meaning.
       */}
      <div className="auth-note">
        <ShieldCheck className="auth-note__icon" size={16} aria-hidden="true" />
        <p className="auth-note__text">
          Resetting your password does not disconnect your banks and never signs out your other
          household members.
        </p>
      </div>
    </AuthCard>
  )
}
